import { isValidElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHouse, faChartPie, faComments, faFilter, faUserPlus, faBolt, faUsers,
    faArrowsRotate, faDollarSign, faCalendarCheck, faFlask, faSliders, faCodeBranch, faGear,
    faCircleInfo, faPlug, faLayerGroup, faBullseye, faClockRotateLeft,
    faTableList, faCubes, faMobileScreen, faChartLine, faDatabase, faClipboardList, faObjectGroup, faCode,
    faRobot, faProjectDiagram, faCircleQuestion, faGraduationCap, faCalendarWeek, faBookOpen, faArrowsSpin, faCircleNodes,
    faBell
} from '@fortawesome/free-solid-svg-icons';

import { ONBOARDING_STEPS, stepPath, stepIndex } from './onboardingSteps.js';
import { PAGES } from '../config/pagePaths.generated.js';
import { settingsPagePath } from '../pages/game/game-settings/settingsAccess.js';

import GameDecisionCenterIcon from '../assets/navbar/gameDashboard.svg?react';

// The pages a single game exposes. `page` is the RBAC page that gates it (nav visibility + route guard);
// pages without one (the game's Homepage and the placeholders) are open to anyone with access to the game.
//
// Settings lists the modules that are standing settings. Game Versions is here too (its own
// registry-first settings view, GameVersionsSettings) — revisited to record/curate builds after setup.
// Game Configs and Invite Your Team, by contrast, are deliberately ABSENT: they are steps of the
// creation wizard (see onboardingSteps.js), reached at /game/new/<id>/<step>, not sidebar pages. Do not
// "fix" that by adding them here.
//
// Backlog and Calendar are not in the nav; their URLs still resolve.
export const GAME_PAGES = [
    { segment: '', label: 'Homepage', icon: faHouse },
    { segment: 'chat', page: PAGES.CHAT, label: 'Chat', icon: faComments },
    {
        label: 'Analytics',
        icon: faChartPie,
        children: [
            // `exact` because /ai-recommendations is a strict prefix of its own AI Workspace
            // (/ai-recommendations/ai-workspace), which is reached from the page rather than the nav.
            { segment: 'ai-recommendations', page: PAGES.GAME_DECISION_CENTER, label: 'AI Recommendations', icon: <GameDecisionCenterIcon />, exact: true },
            { segment: 'dashboard', page: PAGES.GAME_DASHBOARD, label: 'Dashboards', icon: faChartLine },
            // Funnels are out of the nav (2026-10-26); their URLs still open the placeholder page.
            // {
            //     label: 'Funnels',
            //     icon: faFilter,
            //     children: [
            //         { segment: 'acquisition', label: 'Acquisition', icon: faUserPlus },
            //         { segment: 'engagement', label: 'Engagement', icon: faBolt },
            //         { segment: 'retention', label: 'Retention', icon: faArrowsRotate },
            //         { segment: 'monetization', label: 'Monetization', icon: faDollarSign },
            //     ],
            // },
        ],
    },
    {
        label: 'Live Services',
        icon: faCalendarCheck,
        children: [
            { segment: 'live-services', label: 'Overview', icon: faCircleNodes, exact: true },
            { segment: 'live-services/flywheels', page: PAGES.FLYWHEELS, label: 'Flywheels', icon: faArrowsSpin, listsFlywheels: true },
            { segment: 'live-services/experiments', page: PAGES.EXPERIMENTS, label: 'Experiments', icon: faFlask },
            { segment: 'live-services/upcoming', page: PAGES.UPCOMING_EVENTS, label: 'Upcoming Events', icon: faCalendarCheck },
            { segment: 'live-events', page: PAGES.LIVE_EVENTS, label: 'Event Definitions', icon: faCalendarWeek },
            { segment: 'configs', page: PAGES.GAME_CONFIG, label: 'Configs', icon: faCodeBranch },
        ],
    },
    {
        label: 'Players',
        icon: faUsers,
        children: [
            { segment: 'segments', page: PAGES.SEGMENTATION, label: 'Segments', icon: faSliders },
            { segment: 'player-attributes', page: PAGES.PLAYER_ATTRIBUTES, label: 'Player Attributes', icon: faTableList },
        ],
    },
    // Data Intelligence drops the old "Event Intelligence" wrapper: it was a single-child group.
    {
        label: 'Data Intelligence',
        icon: faDatabase,
        children: [
            { segment: 'data-intelligence/events', page: PAGES.EVENTS, label: 'Events Registry', icon: faClipboardList },
            { segment: 'data-intelligence/event-groups', page: PAGES.EVENT_GROUPS, label: 'Event Groups', icon: faObjectGroup },
            { segment: 'data-intelligence/metric-catalog', page: PAGES.GAME_CLIENT_METRIC_CATALOG, label: 'Metric Catalogue', icon: faChartLine },
            // Was "Params (Game Client)" — these are the game's params now, so the qualifier goes.
            { segment: 'data-intelligence/params', page: PAGES.GAME_CLIENT_PARAMS, label: 'Params', icon: faCode },
        ],
    },
    {
        label: 'AI Studio',
        icon: faRobot,
        children: [
            { segment: 'ai-studio/workflow', page: PAGES.AI_WORKFLOW, label: 'Workflow Builder', icon: faProjectDiagram },
            { segment: 'ai-studio/query-library', page: PAGES.QUERY_LIBRARY, label: 'Workflow Prompt Library', icon: faCircleQuestion },
            { segment: 'ai-studio/skill-registry', page: PAGES.SKILL_REGISTRY, label: 'Skill Registry', icon: faGraduationCap },
            { segment: 'ai-studio/alerts', page: PAGES.AI_ALERTS, label: 'Alerts', icon: faBell },
        ],
    },
    {
        label: 'Settings',
        icon: faGear,
        children: [
            { segment: 'settings/details', page: settingsPagePath('details'), label: 'Game Information', icon: faCircleInfo },
            { segment: 'settings/versions', page: settingsPagePath('versions'), label: 'Game Versions', icon: faMobileScreen },
            { segment: 'settings/data', page: settingsPagePath('data'), label: 'Data Connections', icon: faPlug },
            { segment: 'settings/sdk', page: settingsPagePath('sdk'), label: 'SDK Integration', icon: faCubes },
            { segment: 'settings/systems', page: settingsPagePath('systems'), label: 'Game Systems', icon: faLayerGroup },
            { segment: 'settings/objectives', page: settingsPagePath('objectives'), label: 'Objectives', icon: faBullseye },
            { segment: 'settings/history', page: settingsPagePath('history'), label: 'Game History', icon: faClockRotateLeft },
            { segment: 'settings/knowledge', page: settingsPagePath('knowledge'), label: 'Game Knowledge', icon: faBookOpen },
        ],
    },
];

/* `icon` takes a FontAwesome definition OR an already-rendered node, matching Button and Tabs —
   the pages carried over from the old nav keep their own SVGs, and handing one of those to
   FontAwesomeIcon logs "Could not find icon" and renders nothing. */
const navIcon = (icon) => (isValidElement(icon) ? icon : <FontAwesomeIcon icon={icon} />);

const flywheelLinks = (slug, flywheels) => flywheels.map((f) => ({
    path: `/game/${slug}/live-services/flywheels/${encodeURIComponent(f.id)}`,
    label: f.name,
    icon: <FontAwesomeIcon icon={f.icon} style={{ color: f.color }} />,
}));

const toNode = (page, slug, top = false, flywheels = []) => (page.children
    ? {
        section: top,
        label: page.label,
        icon: navIcon(page.icon),
        subItems: page.children.map(c => toNode(c, slug, false, flywheels)),
    }
    : {
        section: top,
        path: `/game/${slug}${page.segment ? `/${page.segment}` : ''}`,
        label: page.label,
        icon: navIcon(page.icon),
        // A segment that is a strict prefix of a sibling's would light up on that sibling too, since
        // the default match is by prefix. `exact` is also what pins the bare /game/<slug> Dashboard.
        exact: page.segment === '' || page.exact === true,
        // RBAC: filterLinksByRole hides the item when this page resolves to NO_ACCESS. Its route carries the
        // same gate (userDashboardRoutes) - a page with no screen yet included, which gets a gated placeholder
        // route of its own - so a hidden page is also unreachable by URL. rbacWiring.test.js holds both to it.
        ...(page.page ? { pagePath: page.page } : {}),
        ...(page.listsFlywheels && flywheels.length ? { subItems: flywheelLinks(slug, flywheels) } : {}),
    });

// The game scope's nav, bound to the game currently in the URL. Every page is its own top-level
// section — the game itself is named by the header's game chip, not by a wrapper section.
export const gamePageLinks = (slug, { flywheels = [] } = {}) => GAME_PAGES.map(p => toNode(p, slug, true, flywheels));

// The onboarding scope's nav: the 10 creation steps, each a top-level section
// linking to /game/new/<gameId>/<key>. Replaces the game list while the draft flow
// is open; `gameId` is the game currently in the URL.
//
// `maxStepKey` is the furthest step reached so far: anything past it is locked, so the
// stepper can only go back over ground already covered, never skip ahead. Unknown (still
// loading) leaves every step open rather than locking the user out of all of them.
export const onboardingStepLinks = (gameId, maxStepKey) => {
    const maxIdx = maxStepKey ? stepIndex(maxStepKey) : ONBOARDING_STEPS.length - 1;
    return ONBOARDING_STEPS.map((s, i) => ({
        section: true,
        path: stepPath(gameId, s.key),
        label: `${i + 1}. ${s.label}`,
        icon: <FontAwesomeIcon icon={s.icon} />,
        exact: true,
        isLocked: i > maxIdx,
    }));
};

// Label for a game page path, so the placeholder titles itself the same way the nav does.
export const gamePageLabel = (segment) => {
    const walk = (pages) => {
        for (const p of pages) {
            if (p.children) {
                const hit = walk(p.children);
                if (hit) return hit;
            } else if (p.segment === segment) {
                return p.label;
            }
        }
        return null;
    };
    return walk(GAME_PAGES);
};
